import { Grid, Typography, Box, Stack } from '@mui/material';

import { useState, useCallback } from 'react';

import { useSelector } from 'react-redux';

import DOMPurify from 'dompurify';

import { ReactComponent as BotAvatar } from 'common/assets/images/bot-avatar.svg';
import { ReactComponent as Copy } from 'common/assets/images/copy.svg';
import { ReactComponent as Regenerate } from 'common/assets/images/regenerate.svg';
import { ReactComponent as UserAvatar } from 'common/assets/images/user-avatar.svg';
import FeedbackForm from 'common/components/FeedbackForm/FeedbackForm';
import FeedbackIconDisplay from 'common/components/FeedbackIconDisplay/FeedbackIconDisplay';
import { MODULES } from 'common/constants';
import {
	BOT_ACTIONS,
	CHAT_USER_OPTIONS,
	TAB_VALUES,
} from 'common/constants/AskRiskLinkConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { RootState } from 'common/store/AppStore';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { IMessageProps } from '../api/type-def';
import { RiskLinkUploadedDocument } from '../OpenRiskGPTUtil';

import ReUploadDocument from './ReUploadDocument';
import RiskGptFeedBackSuccessAlert from './RiskGptFeedBackSuccessAlert';

import styles from './RiskGptMessageView.module.scss';

const RiskGptMessageView = (elements: IMessageProps) => {
	/* Destructured variables */
	const {
		type,
		date,
		message,
		name,
		reloadMessages,
		regenerateLastQueryResponse,
		copyToClipBoard,
		feedback,
		uniqueId,
		time,
		isUploadStatus,
		sources,
		uploadUrl,
		isReUpload,
		handleToastChange,
		sessionId,
		promptRole,
	} = elements;
	const { REGENERATE, COPY } = BOT_ACTIONS;
	const { USER, BOT } = CHAT_USER_OPTIONS;
	const { PROMPT_LIBRARY } = TAB_VALUES;

	const { IS_ASKLINK_FEEDBACK_ENABLED } = FeatureFlagsKeysEnum;
	const { ASK_RISK_LINK } = MODULES;

	const [isLike, setIsLike] = useState<boolean>();
	const [feedbackInfo, setFeedbackInfo] = useState(feedback);
	const [feedBackClicked, setFeedBackClicked] = useState<boolean>(false);
	const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

	const isAskLinkFeedbackEnabled = useFeatureFlag(IS_ASKLINK_FEEDBACK_ENABLED);
	const sourceTitles =
		Array.isArray(sources) && sources?.map((obj) => obj?.title);

	const reUploadHistory = useSelector(
		(state: RootState) => state.common.reUploadHistory,
	);
	const uploadedHistory = useSelector(
		(state: RootState) => state.common.uploadedHistory,
	);

	const submitFeedback = async (feedbackData: any) => {
		setFeedbackInfo(feedbackData);
		setShowSuccessAlert(true);
		reloadMessages();
		setIsLike(undefined);
		setFeedBackClicked(false);
	};

	const cancelFeedback = () => {
		setFeedBackClicked(false);
		setIsLike(undefined);
	};

	const clickLikeFeedBackIcon = () => {
		if (isLike) {
			setFeedBackClicked(false);
			setIsLike(undefined);
		} else {
			setFeedBackClicked(true);
			setIsLike(true);
		}
	};
	const emptyResponse = {
		comment: '',
		isLike,
		id: '',
		reason: '',
	};

	const clickUnLikeFeedBackIcon = () => {
		if (isLike ?? true) {
			setFeedBackClicked(true);
			setIsLike(false);
		} else {
			setFeedBackClicked(false);
			setIsLike(undefined);
		}
	};

	const handleSuccessToast = useCallback(() => {
		setShowSuccessAlert(false);
	}, []);

	const renderUserGridStyles = () =>
		isUploadStatus || isReUpload
			? styles['user-upload-grid']
			: styles['user-message-grid'];

	return (
		<>
			<Grid item display="flex">
				{type?.toLowerCase() === USER?.toLowerCase() ? (
					<UserAvatar />
				) : (
					<Box className={styles['bot-avatar']}>
						<BotAvatar />
					</Box>
				)}
				<Typography
					className={
						type?.toLowerCase() === USER?.toLowerCase()
							? renderUserGridStyles()
							: styles['bot-message-grid']
					}
				>
					{!(isUploadStatus || isReUpload) && (
						<div
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(message?.replace(/\n/g, '<br />')),
							}}
							className={
								type?.toLowerCase() === USER?.toLowerCase()
									? styles['user-text-container']
									: ''
							}
						/>
					)}

					{isUploadStatus && uploadedHistory && uploadedHistory?.length > 0 && (
						<Typography className={styles['user-doc-container']}>
							{uploadedHistory?.map((docFile) => (
								<RiskLinkUploadedDocument file={docFile} key={docFile?.id} />
							))}
						</Typography>
					)}

					{isReUpload && reUploadHistory && (
						<Grid item>
							<ReUploadDocument
								uploadUrl={uploadUrl}
								handleToastChange={handleToastChange}
								sessionId={sessionId}
							/>
						</Grid>
					)}
					{type?.toLowerCase() === BOT?.toLowerCase() && (
						<>
							{promptRole && (
								<Typography className={styles['reference-section']}>
									<Typography className={styles['source-text']}>
										{toSentenceCase(PROMPT_LIBRARY)}:
									</Typography>
									<Typography className={styles['prompt-text']}>
										{promptRole}
									</Typography>
								</Typography>
							)}
							{sourceTitles && sourceTitles?.length > 0 && (
								<Typography className={styles['reference-section']}>
									<Typography className={styles['source-text']}>
										{toSentenceCase('Source:')}
									</Typography>
									<Typography className={styles['reference-text']}>
										{sourceTitles?.length === 1
											? sourceTitles[0]
											: sourceTitles?.join(', ')}
									</Typography>
								</Typography>
							)}
							<Grid container className={styles['chat-root']}>
								<Box className={styles['chat-regenerate-grid']}>
									<Box
										className={styles['risk-gpt-regenerate']}
										onClick={regenerateLastQueryResponse}
										data-testid="regenerate"
									>
										<Regenerate />
										<span className={styles['icon-text']}>
											{toSentenceCase(REGENERATE)}
										</span>
									</Box>
									<Box
										className={styles['risk-gpt-regenerate']}
										onClick={copyToClipBoard}
										data-testid="copy"
									>
										<Copy />
										<span className={styles['icon-text']}>
											{toSentenceCase(COPY)}
										</span>
									</Box>
								</Box>
							</Grid>
						</>
					)}
				</Typography>
			</Grid>
			<Grid item className={styles['user-info-section']}>
				<Stack className={styles['time-stamp-section']}>
					<Typography className={styles['user-info']}>{date}</Typography>
					<Typography className={styles['user-info-subsection']}>
						{time}
					</Typography>
					<Typography className={styles['user-info-seperator']} />
					<Typography className={styles['user-info-subsection']}>
						{name}
					</Typography>
				</Stack>
				{type?.toLowerCase() === BOT?.toLowerCase() && (
					<Box className={styles['chat-feedback-grid']}>
						{isAskLinkFeedbackEnabled && (
							<FeedbackIconDisplay
								previousFeedBack={feedbackInfo || emptyResponse}
								currentFeedBack={emptyResponse}
								clickLikeFeedBack={clickLikeFeedBackIcon}
								clickUnLikeFeedBack={clickUnLikeFeedBackIcon}
							/>
						)}
					</Box>
				)}
			</Grid>
			{feedBackClicked && (
				<FeedbackForm
					module={ASK_RISK_LINK}
					messageId={uniqueId}
					feedbackData={feedbackInfo || emptyResponse}
					isLikeView={isLike || false}
					handleCancelFeedback={cancelFeedback}
					handleSubmitFeedback={submitFeedback}
				/>
			)}
			<RiskGptFeedBackSuccessAlert
				open={showSuccessAlert}
				vertical="bottom"
				horizontal="left"
				handleCloseMessage={handleSuccessToast}
			/>
		</>
	);
};

export default RiskGptMessageView;
